/**
 * SupportModeContext
 *
 * Hält den Zustand einer aktiven Support-Session: ein Plattform-Admin
 * (`role='admin'` + `is_superadmin`) sieht für die Dauer einer Sitzung
 * die Daten einer Kunden-Organisation. Nicht zu verwechseln mit dem
 * Demo-Modus (Mock-Daten) — Support-Modus liest echte Kundendaten und
 * ist DSGVO-relevant: jede Aktion wird in `audit_logs` verzeichnet.
 *
 * Design-Entscheidungen:
 *
 *   • Persistenz in `sessionStorage`, nicht `localStorage` — die Session
 *     endet spätestens mit dem Schließen des Tabs. Verhindert, dass ein
 *     vergessenes Browserfenster wochenlang Kundendaten zeigt.
 *
 *   • Phase 1 ist **read-only**: die App rendert die Sicht der Ziel-Org,
 *     aber Schreibmutationen werden im Repository-Layer geblockt. Die
 *     RLS-Bridge für schreibende Support-Aktionen (mit vollständigem
 *     Audit-Trail pro Mutation) folgt in einem späteren PR.
 *
 *   • Der Provider muss VOR `AuthProvider` und `DemoProvider` montiert
 *     werden, weil `useFeatures` (das `targetOrgId` für die Org-Query
 *     braucht) beide konsumiert.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { setSupportModeActive } from '@/lib/supportModeFlag';

interface SupportSession {
  sessionId: string;
  targetOrgId: string;
  targetOrgName: string;
  reason: string;
  startedAt: string;
}

interface SupportModeContextValue {
  active: boolean;
  session: SupportSession | null;
  startSupport: (args: {
    targetOrgId: string;
    targetOrgName: string;
    reason: string;
  }) => Promise<void>;
  endSupport: () => Promise<void>;
}

const STORAGE_KEY = 'nf_support_session';

const SupportModeContext = createContext<SupportModeContextValue | undefined>(undefined);

function readPersistedSession(): SupportSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SupportSession>;
    if (
      typeof parsed.sessionId === 'string' &&
      typeof parsed.targetOrgId === 'string' &&
      typeof parsed.targetOrgName === 'string' &&
      typeof parsed.reason === 'string' &&
      typeof parsed.startedAt === 'string'
    ) {
      return parsed as SupportSession;
    }
  } catch {
    // Malformed payload — drop it and start fresh.
  }
  return null;
}

function persistSession(session: SupportSession | null) {
  if (typeof window === 'undefined') return;
  if (session) {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } else {
    window.sessionStorage.removeItem(STORAGE_KEY);
  }
}

export function SupportModeProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SupportSession | null>(readPersistedSession);

  useEffect(() => {
    persistSession(session);
    // Spiegele den Context-Status ins Module-Singleton, damit der
    // Supabase-Client-Wrapper Write-Versuche während eines Support-Modus
    // synchron (= im selben Tick wie der Klick) abweisen kann, ohne
    // selbst React-Hooks zu konsumieren.
    setSupportModeActive(session !== null);
  }, [session]);

  // Globaler Toast für geblockte Schreibversuche. Der Wrapper im
  // Supabase-Client wirft einen `SupportModeWriteBlockedError`, der je
  // nach Mutation in einem onError-Handler landet — hier zeigen wir
  // *zusätzlich* eine einheitliche Toast, damit auch nicht-mutations-
  // gekapselte Writes (z. B. direkte `supabase.from(...).insert(...)`-
  // Aufrufe) sichtbares Feedback geben.
  useEffect(() => {
    const handler = () => {
      toast.warning('Im Support-Modus ist nur Lesen erlaubt.', {
        description: 'Beende den Support-Modus, um Daten in deiner eigenen Org zu bearbeiten.',
        duration: 4000,
      });
    };
    window.addEventListener('nf-support-blocked', handler as EventListener);
    return () => window.removeEventListener('nf-support-blocked', handler as EventListener);
  }, []);

  const startSupport = useCallback(
    async ({
      targetOrgId,
      targetOrgName,
      reason,
    }: {
      targetOrgId: string;
      targetOrgName: string;
      reason: string;
    }) => {
      const { data, error } = await supabase.rpc('start_support_session', {
        p_target_org_id: targetOrgId,
        p_reason: reason,
      });
      if (error) throw error;
      const sessionId = data as unknown as string;
      setSession({
        sessionId,
        targetOrgId,
        targetOrgName,
        reason,
        startedAt: new Date().toISOString(),
      });
    },
    [],
  );

  const endSupport = useCallback(async () => {
    if (!session) return;
    const { error } = await supabase.rpc('end_support_session', {
      p_session_id: session.sessionId,
    });
    if (error) throw error;
    setSession(null);
  }, [session]);

  const value = useMemo<SupportModeContextValue>(
    () => ({
      active: session !== null,
      session,
      startSupport,
      endSupport,
    }),
    [session, startSupport, endSupport],
  );

  return <SupportModeContext.Provider value={value}>{children}</SupportModeContext.Provider>;
}

export function useSupportMode(): SupportModeContextValue {
  const ctx = useContext(SupportModeContext);
  if (!ctx) {
    throw new Error('useSupportMode must be used inside <SupportModeProvider>');
  }
  return ctx;
}
